// ✅ src/pages/OrderManagement/ReceivableListing.jsx
// FULL UPDATED FILE
// - Added localStorage helper
// - Added common headers
// - All API calls now send headers from localStorage
// - Keeps pass-full-row behavior to Entry page

import React, { useEffect, useMemo, useState, useContext } from "react";
import axios from "axios";
import { ThemeContext } from "../../context/ThemeContext";
import getCurrentSkin from "../../utils/getCurrentSkin";
import HeaderBar from "../../components/HeaderBar";
import ProjectPostOrderLayoutWrapper from "../../components/ProjectPostOrderLayoutWrapper";

import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

import {
  FaSearch,
  FaEye,
  FaPlus,
  FaTrash,
  FaChevronUp,
  FaChevronDown,
  FaEdit,
  FaExternalLinkAlt,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";

const API_BASE = "http://nissi-india.com/api/receivableListing";

const toStr = (v) => (v === null || v === undefined ? "" : String(v));

const toNum = (v) => {
  const n = Number(String(v ?? "").replace(/,/g, "").trim());
  return Number.isFinite(n) ? n : 0;
};

const fmtMoney2 = (v) =>
  toNum(v).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const ReceivableListing = () => {
  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;
  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const navigate = useNavigate();

  const [showFilters, setShowFilters] = useState(true);
  const [workOrders, setWorkOrders] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedIds, setSelectedIds] = useState([]);

  const [filters, setFilters] = useState({
    fromDate: "",
    toDate: "",
    nameOfWork: "Select",
    contractValue: "",
  });

  const setField = (k, v) => setFilters((p) => ({ ...p, [k]: v }));

  // ✅ localStorage helper
  const getLS = (key, fallback = "") => {
    if (typeof window === "undefined") return fallback;
    const val = localStorage.getItem(key);
    return val !== null && val !== undefined ? val : fallback;
  };

  // ✅ localStorage values
  const finYearId = getLS("finYearId");
  const finYear = getLS("finYear");
  const branchIdLS = getLS("branchId") || getLS("branchid");
  const branchLS = getLS("branchType") || getLS("branchtype");
  const userIdLS =
    getLS("userId") || getLS("loginuserid") || getLS("loginUserId");

  // ✅ parsed values
  const branchId = useMemo(() => Number(branchIdLS || 0), [branchIdLS]);
  const branchType = useMemo(() => String(branchLS || "").trim(), [branchLS]);
  const userId = useMemo(() => String(userIdLS || "").trim(), [userIdLS]);

  // ✅ common headers
  const commonHeaders = useMemo(
    () => ({
      "x-finyearid": finYearId,
      "x-finyear": finYear,
      "x-branchid": branchId,
      "x-branch": branchType,
      "x-userid": userId,
    }),
    [finYearId, finYear, branchId, branchType, userId]
  );

  // ---------- ID + PICK HELPERS ----------
  const getRowId = (row, idx) => {
    const id =
      row.fld_receivableid ??
      row.fld_ReceivableId ??
      row.receivableId ??
      row.ReceivableID ??
      row.id ??
      row.fld_receviableid ?? // typo fallback
      row.fld_receviableid;

    return id ? Number(id) : -(idx + 1);
  };

  const pick = (row, keys, fallback = "") => {
    for (const k of keys) {
      const v = row?.[k];
      if (v !== null && v !== undefined && String(v).trim() !== "") return v;
    }
    return fallback;
  };

  // ✅ payload for Entry page
  const buildRowPayload = (row, idx) => {
    const id = getRowId(row, idx);

    return {
      receivableId: id,
      row,
      mapped: {
        contractId: pick(row, ["fld_contractid", "contractId", "ContractId"], ""),
        contractName: pick(
          row,
          ["fld_contractname", "contractname", "ContractName"],
          ""
        ),
        entryDate: pick(
          row,
          ["fld_entrydate", "entryDate", "EntryDate", "fld_entrydate1"],
          ""
        ),
        workOrderValue: pick(
          row,
          ["fld_workordervalue", "workOrderValue", "WorkOrderValue"],
          ""
        ),
        operativeValue: pick(
          row,
          ["fld_operativevalue", "operativeValue", "OperativeValue"],
          ""
        ),
        billedValue: pick(
          row,
          ["fld_billedvalue", "billedValue", "BilledValue"],
          ""
        ),
        raBills: pick(
          row,
          ["fld_RAbills", "fld_rabills", "raBills", "RABills"],
          ""
        ),
        updatedDate: pick(
          row,
          ["fld_updateddate", "updatedDate", "UpdatedDate", "fld_updateddate1"],
          ""
        ),
        balanceBill: pick(
          row,
          ["fld_balancebill", "balanceBill", "BalanceBill"],
          ""
        ),
        remarks: pick(row, ["fld_remarks", "remarks", "Remarks"], ""),
        completionDate: pick(
          row,
          ["fld_completiondate", "completionDate", "CompletionDate", "fld_completiondate1"],
          ""
        ),
      },
    };
  };

  // ---------- API CALLS ----------
  const loadWorkOrders = async () => {
    try {
      const res = await axios.get(`${API_BASE}/work-orders`, {
        headers: commonHeaders,
      });

      if (res.data?.success) setWorkOrders(res.data?.data || []);
      else setWorkOrders([]);
    } catch (e) {
      console.error(e);
      setWorkOrders([]);
      toast.error("Failed to load Name of Work");
    }
  };

  const fetchList = async (payload) => {
    setLoading(true);
    try {
      const res = await axios.post(`${API_BASE}/list`, payload, {
        headers: commonHeaders,
      });

      if (res.data?.success) {
        setRows(res.data?.data || []);
        setSelectedIds([]);
      } else {
        setRows([]);
        toast.warning(res.data?.message || "No records");
      }
    } catch (e) {
      console.error(e);
      setRows([]);
      toast.error(e?.response?.data?.message || "Failed to load receivable list");
    } finally {
      setLoading(false);
    }
  };

  const viewAll = async () => {
    setLoading(true);
    try {
      const res = await axios.post(
        `${API_BASE}/viewAll`,
        {},
        {
          headers: commonHeaders,
        }
      );

      if (res.data?.success) {
        setRows(res.data?.data || []);
        setSelectedIds([]);
        toast.info("Loaded all records");
      } else {
        setRows([]);
        toast.warning(res.data?.message || "No records");
      }
    } catch (e) {
      console.error(e);
      setRows([]);
      toast.error(e?.response?.data?.message || "Failed to load all receivables");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadWorkOrders();
    viewAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [commonHeaders]);

  // ---------- SEARCH / VIEWALL ----------
  const handleSearch = () => {
    fetchList({
      nameOfWork: filters.nameOfWork || "Select",
      contractValue: filters.contractValue || "",
      fromDate: filters.fromDate || "",
      toDate: filters.toDate || "",
    });
  };

  const handleViewAll = () => {
    setFilters({
      fromDate: "",
      toDate: "",
      nameOfWork: "Select",
      contractValue: "",
    });
    viewAll();
  };

  const handleAddNew = () => {
    sessionStorage.removeItem("receivableEntryPayload");
    sessionStorage.removeItem("receivableEntryMultiPayload");
    navigate("/OrderManagement/ReceivableEntry");
  };

  // ✅ PASS SINGLE ROW TO ENTRY
  const handleEdit = (row, idx) => {
    const payload = buildRowPayload(row, idx);

    sessionStorage.setItem("receivableEntryPayload", JSON.stringify(payload));
    sessionStorage.removeItem("receivableEntryMultiPayload");

    navigate("/OrderManagement/ReceivableEntry", { state: payload });
  };

  // ✅ PASS MULTI SELECTED ROWS TO ENTRY
  const openSelected = () => {
    if (!selectedIds.length) {
      toast.warning("Please select at least one item");
      return;
    }

    const selectedPayloads = rows
      .map((r, idx) => ({ r, idx }))
      .filter(({ r, idx }) => selectedIds.includes(getRowId(r, idx)))
      .map(({ r, idx }) => buildRowPayload(r, idx));

    sessionStorage.setItem(
      "receivableEntryMultiPayload",
      JSON.stringify(selectedPayloads)
    );
    sessionStorage.removeItem("receivableEntryPayload");

    navigate("/OrderManagement/ReceivableEntry", {
      state: { multi: true, rows: selectedPayloads },
    });
  };

  // ---------- CHECKBOX ----------
  const allSelected = useMemo(() => {
    if (!rows.length) return false;
    const ids = rows.map((r, idx) => getRowId(r, idx));
    return ids.every((id) => selectedIds.includes(id));
  }, [rows, selectedIds]);

  const toggleSelectAll = () => {
    if (!rows.length) return;
    if (allSelected) setSelectedIds([]);
    else setSelectedIds(rows.map((r, idx) => getRowId(r, idx)));
  };

  const toggleSelectOne = (id) => {
    setSelectedIds((p) =>
      p.includes(id) ? p.filter((x) => x !== id) : [...p, id]
    );
  };

  // ---------- DELETE ----------
  const handleDelete = async () => {
    if (!selectedIds.length) {
      toast.warning("Please select items to delete");
      return;
    }

    const ok = window.confirm(`Delete ${selectedIds.length} selected record(s)?`);
    if (!ok) return;

    try {
      const res = await axios.post(
        `${API_BASE}/delete`,
        { ids: selectedIds },
        { headers: commonHeaders }
      );

      if (res.data?.success) {
        toast.success(res.data?.message || "Deleted");
        fetchList({
          nameOfWork: filters.nameOfWork || "Select",
          contractValue: filters.contractValue || "",
          fromDate: filters.fromDate || "",
          toDate: filters.toDate || "",
        });
      } else {
        toast.error(res.data?.message || "Delete failed");
      }
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.message || "Delete failed");
    }
  };

  return (
    <ProjectPostOrderLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">RECEIVABLE LISTING</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : "bg-white text-black"
          }`}
        >
          <div className="flex flex-col md:flex-row justify-between md:items-center gap-4 mb-6">
            <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
              Receivable Listing
            </h1>

            <div className="flex flex-wrap items-center gap-3 justify-end">
              <button
                onClick={() => setShowFilters((s) => !s)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${
                  isDark
                    ? "bg-gray-700 hover:bg-gray-600 text-white border border-gray-600"
                    : "bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300"
                }`}
              >
                {showFilters ? <FaChevronUp /> : <FaChevronDown />}
                {showFilters ? "Hide Filters" : "Show Filters"}
              </button>

              {selectedIds.length > 0 && (
                <button
                  onClick={openSelected}
                  className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${buttonClass} ${hoverClass} text-white`}
                >
                  <FaExternalLinkAlt />
                  Open Selected ({selectedIds.length})
                </button>
              )}

              <button
                onClick={handleAddNew}
                className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${buttonClass} ${hoverClass} text-white`}
              >
                <FaPlus />
                Add New
              </button>
            </div>
          </div>

          {showFilters && (
            <div
              className={`p-6 rounded-xl border mb-6 ${
                isDark ? "bg-gray-800 border-gray-700" : "bg-gray-50 border-gray-200"
              }`}
            >
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="space-y-4">
                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${
                        isDark ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      From Date
                    </label>
                    <input
                      type="date"
                      value={filters.fromDate}
                      onChange={(e) => setField("fromDate", e.target.value)}
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                        isDark
                          ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                          : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                      }`}
                    />
                  </div>

                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${
                        isDark ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Name of the Work
                    </label>
                    <select
                      value={filters.nameOfWork}
                      onChange={(e) => setField("nameOfWork", e.target.value)}
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                        isDark
                          ? "bg-gray-700 border-gray-600 text-white"
                          : "bg-white border-gray-300 text-gray-900"
                      }`}
                    >
                      <option value="Select">Select</option>
                      {workOrders.map((w) => (
                        <option key={w.id} value={String(w.id)}>
                          {w.name}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="space-y-4">
                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${
                        isDark ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      To Date
                    </label>
                    <input
                      type="date"
                      value={filters.toDate}
                      onChange={(e) => setField("toDate", e.target.value)}
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                        isDark
                          ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                          : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                      }`}
                    />
                  </div>

                  <div>
                    <label
                      className={`block text-sm font-medium mb-2 ${
                        isDark ? "text-gray-300" : "text-gray-700"
                      }`}
                    >
                      Contract Value
                    </label>
                    <input
                      type="text"
                      value={filters.contractValue}
                      onChange={(e) => setField("contractValue", e.target.value)}
                      className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all ${
                        isDark
                          ? "bg-gray-700 border-gray-600 text-white placeholder-gray-400"
                          : "bg-white border-gray-300 text-gray-900 placeholder-gray-500"
                      }`}
                      placeholder="Enter contract value"
                    />
                  </div>
                </div>
              </div>

              <div className="flex flex-wrap justify-center items-center mt-6 gap-4">
                <button
                  onClick={handleSearch}
                  className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${buttonClass} ${hoverClass} text-white`}
                >
                  <FaSearch />
                  Search
                </button>

                <button
                  onClick={handleViewAll}
                  className={`flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 ${buttonClass} ${hoverClass} text-white`}
                >
                  <FaEye />
                  View All
                </button>
              </div>
            </div>
          )}

          <div
            className={`rounded-xl shadow-sm border overflow-hidden ${
              isDark ? "border-gray-700" : "border-gray-200"
            }`}
          >
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead className={`${isDark ? "bg-gray-800" : "bg-gray-100"}`}>
                  <tr>
                    <th className="px-4 py-3 text-left">
                      <input type="checkbox" checked={allSelected} onChange={toggleSelectAll} />
                    </th>
                    <th className="px-4 py-3 text-left">S.No</th>
                    <th className="px-4 py-3 text-left">Name of Work</th>
                    <th className="px-4 py-3 text-left">Entry Date</th>
                    <th className="px-4 py-3 text-right">Work Order Value</th>
                    <th className="px-4 py-3 text-right">Operative Value</th>
                    <th className="px-4 py-3 text-right">Billed Value</th>
                    <th className="px-4 py-3 text-right">Balance To Be Billed</th>
                    <th className="px-4 py-3 text-left">Upto Completion</th>
                    <th className="px-4 py-3 text-left">Completion Date</th>
                    <th className="px-4 py-3 text-center">Edit</th>
                  </tr>
                </thead>

                <tbody className={`${isDark ? "bg-gray-900" : "bg-white"}`}>
                  {loading ? (
                    <tr>
                      <td colSpan={11} className="px-4 py-6 text-center">
                        Loading...
                      </td>
                    </tr>
                  ) : rows.length === 0 ? (
                    <tr>
                      <td colSpan={11} className="px-4 py-6 text-center">
                        No records found
                      </td>
                    </tr>
                  ) : (
                    rows.map((row, idx) => {
                      const id = getRowId(row, idx);
                      const sNo = idx + 1;

                      const nameOfWork = pick(row, [
                        "fld_contractname",
                        "contractname",
                        "NameOfWork",
                        "fld_nameofwork",
                      ]);
                      const entryDate = pick(row, [
                        "fld_entrydate",
                        "entryDate",
                        "EntryDate",
                        "fld_entrydate1",
                      ]);
                      const workOrderValue = pick(
                        row,
                        ["fld_workordervalue", "workOrderValue", "WorkOrderValue"],
                        0
                      );
                      const operativeValue = pick(
                        row,
                        ["fld_operativevalue", "operativeValue", "OperativeValue"],
                        0
                      );
                      const billedValue = pick(
                        row,
                        ["fld_billedvalue", "billedValue", "BilledValue"],
                        0
                      );
                      const balanceToBeBilled = pick(
                        row,
                        ["fld_balancebill", "balanceBill", "BalanceBill"],
                        0
                      );
                      const uptoCompletion = pick(row, [
                        "fld_uptocompletion",
                        "uptoCompletion",
                        "UptoCompletion",
                      ]);
                      const completionDate = pick(row, [
                        "fld_completiondate",
                        "completionDate",
                        "CompletionDate",
                      ]);

                      return (
                        <tr
                          key={id}
                          className={`border-t ${
                            isDark
                              ? "border-gray-800 hover:bg-gray-800/40"
                              : "border-gray-200 hover:bg-gray-50"
                          }`}
                        >
                          <td className="px-4 py-3">
                            <input
                              type="checkbox"
                              checked={selectedIds.includes(id)}
                              onChange={() => toggleSelectOne(id)}
                            />
                          </td>
                          <td className="px-4 py-3">{sNo}</td>
                          <td className="px-4 py-3">{toStr(nameOfWork)}</td>
                          <td className="px-4 py-3">{toStr(entryDate)}</td>
                          <td className="px-4 py-3 text-right">{fmtMoney2(workOrderValue)}</td>
                          <td className="px-4 py-3 text-right">{fmtMoney2(operativeValue)}</td>
                          <td className="px-4 py-3 text-right">{fmtMoney2(billedValue)}</td>
                          <td className="px-4 py-3 text-right">
                            {fmtMoney2(balanceToBeBilled)}
                          </td>
                          <td className="px-4 py-3">{toStr(uptoCompletion)}</td>
                          <td className="px-4 py-3">{toStr(completionDate)}</td>
                          <td className="px-4 py-3 text-center">
                            <button
                              onClick={() => handleEdit(row, idx)}
                              className={`inline-flex items-center gap-2 px-3 py-1 rounded-lg text-xs font-semibold transition-all ${
                                isDark
                                  ? "bg-blue-600 hover:bg-blue-700 text-white"
                                  : "bg-blue-100 hover:bg-blue-200 text-blue-700"
                              }`}
                            >
                              <FaEdit />
                              Edit
                            </button>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {selectedIds.length > 0 && (
            <div className="flex justify-end mt-4">
              <button
                onClick={handleDelete}
                className="flex items-center gap-2 px-6 py-2 rounded-lg font-semibold shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 transition-all duration-200 bg-red-600 hover:bg-red-700 text-white"
              >
                <FaTrash />
                Delete Selected ({selectedIds.length})
              </button>
            </div>
          )}

          <ToastContainer
            position="top-right"
            autoClose={2500}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </ProjectPostOrderLayoutWrapper>
  );
};

export default ReceivableListing;